import { RefValueKind } from '@repo/sdk/models/components';

import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import {
  BOOLEAN_LABEL_SETS,
  type BooleanLabelTarget,
  isBooleanLike,
  resolveBooleanValue,
} from '@/helpers/unit-formatter/transformers/coerce-boolean-like';

/**
 * Whether a setting is boolean at all, so a caller holding any setting — the save
 * dialog builds rows for every kind — can ask before reaching for a vocabulary.
 * Mirrors `isBooleanMetricDisplay`: the platform marks it on the ref, and some
 * settings only say so through their display units.
 */
export function isBooleanSetting(setting: EnhancedSetting): boolean {
  if (setting?.ref?.valueKind === RefValueKind.Boolean) return true;
  if (setting?.units?.toLowerCase() === 'boolean') return true;

  const declared = setting?.displayUnits?.toLowerCase();
  return declared != null && declared in BOOLEAN_LABEL_SETS;
}

/**
 * The vocabulary a boolean setting reads in. `on-off` is the default rather than
 * `boolean`: a setpoint that says ON / OFF is what an operator expects, where
 * TRUE / FALSE reads like a debug value. A setting that names its own vocabulary —
 * `open-closed` for a valve — keeps it.
 *
 * `boolean` and `string` are the catalog's names for "no opinion", so they resolve
 * to the default too.
 */
export function resolveBooleanTarget(setting: EnhancedSetting): BooleanLabelTarget {
  const declared = setting?.displayUnits?.toLowerCase();
  if (declared && declared !== 'boolean' && declared !== 'string') {
    if (declared in BOOLEAN_LABEL_SETS) return declared as BooleanLabelTarget;
  }
  return 'on-off';
}

export interface BooleanSettingVocabulary {
  /** The two labels the field offers, in the setting's own vocabulary. */
  offLabel: string;
  onLabel: string;
  /** A stored value -> the matching label, or '' when it cannot be read. */
  fromStored: (value: unknown) => string;
  /** One of the two labels -> what gets stored. Identity: the label IS the value. */
  toStored: (selected: string) => string;
}

/**
 * Reads a boolean setting in its own vocabulary.
 *
 * There is no separate display vocabulary to translate to and from: the options,
 * the field's value and what the form stores are all the same two labels. That is
 * deliberate — an earlier version offered a fixed Off / On and converted at the
 * edges, which made an `open-closed` setting read "On" in the field while the save
 * dialog read "OPEN" for the same change.
 *
 * Reading a stored value goes through the canonical resolver, so every shape the
 * platform may hold is accepted — a raw boolean, `0`/`1`, or any label from any of
 * the sets (`TRUE`, `ON`, `OPEN`, case-insensitive). Anything it cannot read gives
 * `''`, which shows the placeholder rather than guessing a value an operator might
 * then save.
 */
export function getBooleanSettingVocabulary(setting: EnhancedSetting): BooleanSettingVocabulary {
  const labels = BOOLEAN_LABEL_SETS[resolveBooleanTarget(setting)];

  return {
    offLabel: labels.false,
    onLabel: labels.true,
    fromStored: (value) => {
      if (!isBooleanLike(value)) return '';
      return resolveBooleanValue(value) ? labels.true : labels.false;
    },
    toStored: (selected) => selected,
  };
}
